import {
  calculateDeadlineFromOfferCargoDestination,
  checkProposedDeadlineSatisfied,
  useformatDeadline,
} from "@/features/offer-cargo-destination-deadline";
import type {
  CarrierDeliveryProposalCreatePageProps,
  PreviewDeliveryProposalOfferCargoDestination,
} from "@/pages/carrier-delivery-proposal-create/model";
import type { LoadableData } from "@/shared/lib/loadable";
import { BaseMessageCard } from "@/shared/ui/base-message-card";
import { BasePaper } from "@/shared/ui/base-paper";
import { DeliveryProposalFormTable } from "@/widgets/delivery-proposal-form-table";
import { MobileDeliveryProposalFormTable } from "@/widgets/delivery-proposal-form-table/mobile-delivery-proposal-form-table";
import { DeliveryRouteTaskContentTags } from "@/widgets/delivery-route-task-content-tags";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

export const DestinationForm = (props: {
  taskContent: "loading" | "transit" | "unloading";
  draftOfferCargo: LoadableData<
    CarrierDeliveryProposalCreatePageProps["draftOfferCargo"]
  >;
  draftOfferCargoDestination: LoadableData<
    CarrierDeliveryProposalCreatePageProps["draftOfferCargo"]
  >["destinations"][0];
  previewDestinationValues: PreviewDeliveryProposalOfferCargoDestination;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { previewDestinationValues } = props;

  const title =
    props.taskContent === "loading"
      ? "【積地】"
      : props.taskContent === "transit"
        ? "【経由地】"
        : "【卸地】";

  const draftDeadlineValues = calculateDeadlineFromOfferCargoDestination(
    props.taskContent,
    props.draftOfferCargo,
    props.draftOfferCargoDestination,
  );
  const { deadlineText: draftDeadlineText } =
    useformatDeadline(draftDeadlineValues);
  const proposalDeadlineValues = {
    ...previewDestinationValues,
  };
  const { deadlineText: proposalDeadlineText } = useformatDeadline(
    proposalDeadlineValues,
  );
  const isProposedDeadlineSatisfiedDraft = checkProposedDeadlineSatisfied(
    draftDeadlineValues,
    proposalDeadlineValues,
  );

  const deadlineCells = [
    draftDeadlineText,
    <Stack key="deadline-proposed">
      <Typography variant="body1">{proposalDeadlineText}</Typography>
      {!isProposedDeadlineSatisfiedDraft && (
        <BaseMessageCard
          message="荷物の要件を満たしていません。"
          status="warning"
          size="small"
        />
      )}
    </Stack>,
  ];

  const addressCells = [
    `${props.draftOfferCargoDestination.prefecture} ${props.draftOfferCargoDestination.address}`,
    null,
  ];

  const taskDetailCells = [
    props.draftOfferCargoDestination.taskDetail
      ? t(`taskDetail.${props.draftOfferCargoDestination.taskDetail}`)
      : null,
    null,
  ];

  const memoCells = [props.draftOfferCargoDestination.memo, null];

  return (
    <BasePaper sx={{ p: 2, backgroundColor: theme.palette.grey[50] }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={0} alignItems={"center"}>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {title} 作業内容：
          </Typography>
          <DeliveryRouteTaskContentTags
            hasLoading={!!props.draftOfferCargoDestination.hasLoading}
            hasUnloading={!!props.draftOfferCargoDestination.hasUnloading}
          />
        </Stack>

        {/* PC向けのフォーム */}
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <DeliveryProposalFormTable
            headers={["荷物", "提案"]}
            rows={[
              {
                title: "積日時/発日時",
                cells: deadlineCells,
              },
              {
                title: "積地/発地",
                cells: addressCells,
              },
              {
                title: t("offerCargo.desitination.taskDetail"),
                cells: taskDetailCells,
              },
              {
                title: "特記事項(日付等)",
                cells: memoCells,
              },
            ]}
          />
        </Box>

        {/* モバイル向けのフォーム */}
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <MobileDeliveryProposalFormTable
            items={[
              {
                label: "積日時/発日時",
                cargo: deadlineCells[0],
                proposal: deadlineCells[1],
              },
              {
                label: "積地/発地",
                cargo: addressCells[0],
                proposal: addressCells[1],
              },
              {
                label: t("offerCargo.desitination.taskDetail"),
                cargo: taskDetailCells[0],
                proposal: taskDetailCells[1],
              },
              {
                label: "特記事項(日付等)",
                cargo: memoCells[0],
                proposal: memoCells[1],
              },
            ]}
          />
        </Box>
      </Stack>
    </BasePaper>
  );
};
